import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Radar, CheckCircle2, XCircle } from "lucide-react";

function SourcePill({ name, ok }) {
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-4 py-3 ${ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid={`source-${name}`}>
      {ok ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
      <div>
        <p className="font-semibold capitalize">{name}</p>
        <p className="text-xs text-muted-foreground">{ok ? "Configured" : "Not configured"}</p>
      </div>
    </div>
  );
}

export default function AdminMatcher() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/matcher-health"); setD(data); }
    catch (e) { logger.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const cards = [
    { label: "Matches (24h)", value: d.matches_24h },
    { label: "Matches (total)", value: d.matches_total },
    { label: "Active users", value: d.active_users, sub: `${d.total_users_with_prefs} with prefs` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-matcher">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Radar className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Job Matcher Health</h1>
          <p className="text-muted-foreground text-sm">Source connectivity and matching activity. Nightly run ~05:00 UTC.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="matcher-refresh-btn"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Job sources</p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <SourcePill name="adzuna" ok={d.sources?.adzuna} />
          <SourcePill name="jooble" ok={d.sources?.jooble} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="border-zinc-200"><CardContent className="p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className="font-heading text-3xl font-bold mt-1" data-testid={`matcher-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{c.value}</p>
            {c.sub && <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>}
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-zinc-200"><CardContent className="p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Last match delivered</p>
        <p className="font-medium mt-1" data-testid="matcher-last-run">{d.last_match_at ? new Date(d.last_match_at).toLocaleString() : "No matches yet"}</p>
      </CardContent></Card>
    </div>
  );
}
